import { useEditContext, useTranslate } from "react-admin";
import { Dispatch, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Box } from "@mui/material";

const CustomTimeInput = ({
  source,
  label,
  increment = 15,
  autoGeneratedDate,
  setForceRenderParentComponent,
  day,
  selectedDayToRemove,
  form,
  timezone,
  isInvalidTime
}: {
  source: string;
  label: string;
  increment?: number;
  autoGeneratedDate?: Dayjs;
  setForceRenderParentComponent: Dispatch<React.SetStateAction<boolean>>;
  day?: string;
  selectedDayToRemove?: string;
  form: any;
  timezone: string;
  isInvalidTime: boolean;
}) => {
  const translate = useTranslate();
  const { record } = useEditContext();
  const [time, setTime] = useState<Dayjs | null>(
    record?.[source] ? dayjs(record?.[source]).tz(timezone) : null
  );

  useEffect(() => {
    if (!!autoGeneratedDate) {
      setTime(autoGeneratedDate);
      form.setValue(source, autoGeneratedDate, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGeneratedDate]);

  useEffect(() => {
    if (selectedDayToRemove === day) {
      setTime(null);
      form.setValue(source, "", { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDayToRemove]);

  const handleChange = (date: Dayjs | null) => {
    if (date) {
      const selectedTime = dayjs(date).tz(timezone);

      if (selectedTime.minute() % increment !== 0) {
        const roundedTime = selectedTime.minute(
          Math.round(selectedTime.minute() / increment) * increment
        );

        setTime(roundedTime);
        form.setValue(source, roundedTime.toDate());
      } else {
        setTime(selectedTime);
        form.setValue(source, date?.format(), { shouldDirty: true });
      }
    } else {
      setTime(null);
      form.setValue(source, null);
    }
    if (setForceRenderParentComponent) {
      setForceRenderParentComponent((prev) => !prev);
    }
  };

  return (
    <Box
      sx={{
        "& .MuiFilledInput-root": {
          borderRadius: "8px",
          border: "1px solid rgba(48, 47, 46, 0.06)",
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.06)"
          },
          "& fieldset": {
            border: "solid 1px transparent"
          },
          "&:hover fieldset": {
            border: "solid 1px transparent"
          },
          "&.Mui-focused fieldset": {
            border: "solid 1px #b8722a"
          }
        },
        "& .MuiFilledInput-input": {
          height: "18px",
          fontSize: "16px",
          borderRadius: "8px",
          fontFamily: "Inter",
          border: "none",
          paddingTop: "22px",
          paddingBottom: "10px"
        }
      }}
    >
      <TimePicker
        value={time ? (dayjs(time).tz(timezone) as any) : null}
        label={translate(label)}
        onChange={handleChange}
        timezone={timezone}
        minutesStep={increment}
        ampmInClock
        ampm
        skipDisabled
        referenceDate={dayjs.tz("1970-01-01T00:00:00", timezone)}
        sx={{
          width: "200px",
          height: "53px"
        }}
        slotProps={{ textField: { variant: "filled", error: isInvalidTime } }}
      />
    </Box>
  );
};

export default CustomTimeInput;
